import { motion } from "framer-motion";
import { TrendingDown, TrendingUp, DollarSign } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatEur, formatPct } from "@/lib/index";

interface KPICardsProps {
  grossP1: number;
  grossP2: number;
  netP1: number;
  netP2: number;
  savingsPct: number;
}

export function KPICards({ grossP1, grossP2, netP1, netP2, savingsPct }: KPICardsProps) {
  const cards = [
    {
      title: "Coste Bruto P1",
      value: grossP1,
      icon: DollarSign,
      color: "text-success",
      bgColor: "bg-success/10",
      borderColor: "border-success/20"
    },
    {
      title: "Coste Bruto P2",
      value: grossP2,
      icon: DollarSign,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      borderColor: "border-destructive/20"
    },
    {
      title: "Coste Neto P1",
      value: netP1,
      icon: TrendingDown,
      color: "text-primary",
      bgColor: "bg-primary/10",
      borderColor: "border-primary/20"
    },
    {
      title: "Ahorro %",
      value: savingsPct,
      icon: TrendingUp,
      color: "text-success",
      bgColor: "bg-success/10",
      borderColor: "border-success/20",
      isPercentage: true,
      showBadge: true
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 35
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
    >
      {cards.map((card, index) => {
        const Icon = card.icon;
        return (
          <motion.div key={index} variants={cardVariants}>
            <Card
              className={`p-6 border ${card.borderColor} ${card.bgColor} transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]`}
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Icon className={`h-5 w-5 ${card.color}`} />
                  {card.showBadge && (
                    <Badge className="bg-success text-success-foreground text-base px-3 py-1">
                      {formatPct(card.value)}
                    </Badge>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    {card.title}
                  </p>
                  <p className={`text-2xl lg:text-3xl font-bold font-mono ${card.color}`}>
                    {card.isPercentage ? formatPct(card.value) : formatEur(card.value)}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        );
      })}
    </motion.div>
  );
}